import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress,
  Divider, InputAdornment, IconButton, Tooltip, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, Switch, FormControlLabel, List, ListItemButton,
  ListItemText, ListItemIcon, Collapse, Snackbar,
} from '@mui/material';
import WebhookIcon        from '@mui/icons-material/Webhook';
import SaveIcon           from '@mui/icons-material/Save';
import SendIcon           from '@mui/icons-material/Send';
import ContentCopyIcon    from '@mui/icons-material/ContentCopy';
import SecurityIcon       from '@mui/icons-material/Security';
import ShieldIcon         from '@mui/icons-material/Shield';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import WindowIcon         from '@mui/icons-material/Window';
import ExpandMoreIcon     from '@mui/icons-material/ExpandMore';
import ExpandLessIcon     from '@mui/icons-material/ExpandLess';
import LockOpenIcon       from '@mui/icons-material/LockOpen';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────
const ROLE_COLORS = { super_admin: '#7c3aed', admin: '#3b82f6', operator: '#16a34a', readonly: '#6b778c' };
const ROLE_LEVEL  = { readonly: 1, operator: 2, admin: 3, super_admin: 4 };

const PERM_MATRIX = [
  { key: 'view',            label: 'View Dashboard & Tenants',     readonly: true,  operator: true,  admin: true,  super_admin: true  },
  { key: 'audit',           label: 'View Audit Log',               readonly: true,  operator: true,  admin: true,  super_admin: true  },
  { key: 'scan',            label: 'Run Vulnerability Scan',       readonly: true,  operator: true,  admin: true,  super_admin: true  },
  { key: 'sync',            label: 'Sync Tenants',                 readonly: false, operator: true,  admin: true,  super_admin: true  },
  { key: 'deploy',          label: 'Deploy Frontend Updates',      readonly: false, operator: true,  admin: true,  super_admin: true  },
  { key: 'edit_tenants',    label: 'Edit Tenant Settings',         readonly: false, operator: false, admin: true,  super_admin: true  },
  { key: 'sync_lock',       label: 'Manage Sync Lock',             readonly: false, operator: false, admin: true,  super_admin: true  },
  { key: 'manage_users',    label: 'Manage Users',                 readonly: false, operator: false, admin: true,  super_admin: true  },
  { key: 'manage_settings', label: 'Configure Settings',           readonly: false, operator: false, admin: true,  super_admin: true  },
  { key: 'reset_mfa',       label: 'Reset MFA for Users',          readonly: false, operator: false, admin: false, super_admin: true  },
  { key: 'create_users',    label: 'Create / Delete Users',        readonly: false, operator: false, admin: false, super_admin: true  },
];

// Permissions that can be individually overridden (super_admin is fixed)
const OVERRIDEABLE = PERM_MATRIX.filter(p => p.key !== 'view');

const SECTIONS = [
  { id: 'alerts',      label: 'Alerts',              icon: <WebhookIcon fontSize="small" /> },
  { id: 'security',    label: 'Security & MFA',      icon: <SecurityIcon fontSize="small" /> },
  { id: 'roles',       label: 'Roles & Permissions', icon: <ShieldIcon fontSize="small" /> },
  { id: 'overrides',   label: 'User Overrides',      icon: <ManageAccountsIcon fontSize="small" /> },
  { id: 'sso',         label: 'SSO',                 icon: <WindowIcon fontSize="small" /> },
];

function Check({ val }) {
  return val
    ? <CheckCircleIcon sx={{ fontSize: 18, color: '#16a34a' }} />
    : <CancelIcon      sx={{ fontSize: 18, color: 'action.disabled' }} />;
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Settings() {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'super_admin';
  const [section, setSection] = useState('alerts');
  const [loading, setLoading] = useState(true);
  const [users, setUsers]     = useState([]);
  const [snack, setSnack]     = useState({ open: false, msg: '', sev: 'success' });

  // Alerts state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveErr, setSaveErr]       = useState('');
  const [saved, setSaved]           = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState(null);

  // MFA state
  const [mfaMsg, setMfaMsg] = useState({});

  // SSO state
  const [sso, setSso]       = useState({ enabled: false, client_id: '', tenant_id: '', client_secret: '', redirect_uri: '', frontend_url: '' });
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoSaved, setSsoSaved]   = useState(false);

  // User overrides
  const [expanded, setExpanded] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState({});

  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get('/admin-settings'), api.get('/users')]);
      setWebhookUrl(s.data.webhook_url?.value || '');
      setSso({
        enabled:      s.data.sso_enabled?.value === 'true',
        client_id:    s.data.sso_client_id?.value    || '',
        tenant_id:    s.data.sso_tenant_id?.value    || '',
        client_secret: s.data.sso_client_secret?.value || '',
        redirect_uri: s.data.sso_redirect_uri?.value || '',
        frontend_url: s.data.sso_frontend_url?.value || '',
      });
      setUsers(u.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Alerts save/test ──────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setSaved(false); setSaveErr('');
    try {
      await api.put('/admin-settings', { key: 'webhook_url', value: webhookUrl });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) { setSaveErr(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      await api.post('/admin-settings/test-webhook', { url: webhookUrl });
      setTestResult({ ok: true, message: 'Webhook fired - check your Teams channel.' });
    } catch (e) { setTestResult({ ok: false, message: e.message || 'Test failed' }); }
    finally { setTesting(false); }
  };

  // ── SSO save ──────────────────────────────────────────────────────────────
  const handleSsoSave = async () => {
    setSsoSaving(true); setSsoSaved(false);
    try {
      await Promise.all([
        api.put('/admin-settings', { key: 'sso_enabled',      value: String(sso.enabled) }),
        api.put('/admin-settings', { key: 'sso_client_id',    value: sso.client_id }),
        api.put('/admin-settings', { key: 'sso_tenant_id',    value: sso.tenant_id }),
        api.put('/admin-settings', { key: 'sso_client_secret', value: sso.client_secret }),
        api.put('/admin-settings', { key: 'sso_redirect_uri', value: sso.redirect_uri }),
        api.put('/admin-settings', { key: 'sso_frontend_url', value: sso.frontend_url }),
      ]);
      setSsoSaved(true); setTimeout(() => setSsoSaved(false), 3000);
      notify('SSO settings saved');
    } catch { notify('Failed to save SSO settings', 'error'); }
    finally { setSsoSaving(false); }
  };

  // ── Permission override toggle ─────────────────────────────────────────
  const toggleOverride = async (userId, permKey, currentOverrides, roleDefault) => {
    const current = currentOverrides[permKey];
    // Cycle: undefined → override(opposite of default) → undefined
    let next;
    if (current === undefined || current === null) {
      next = !roleDefault; // grant if default is deny, deny if default is grant
    } else {
      next = undefined; // remove override, go back to role default
    }

    const newOverrides = { ...currentOverrides };
    if (next === undefined) delete newOverrides[permKey];
    else newOverrides[permKey] = next;

    setOverrideSaving(s => ({ ...s, [userId]: true }));
    try {
      await api.put(`/users/${userId}`, { permission_overrides: newOverrides });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, permission_overrides: newOverrides } : u));
    } catch { notify('Failed to save override', 'error'); }
    finally { setOverrideSaving(s => ({ ...s, [userId]: false })); }
  };

  const getEffective = (user, permKey) => {
    const p = PERM_MATRIX.find(x => x.key === permKey);
    const roleDefault = p ? p[user.role] : false;
    const ov = user.permission_overrides?.[permKey];
    if (ov !== undefined && ov !== null) return { value: ov, overridden: true };
    return { value: roleDefault, overridden: false };
  };

  // ── Layout ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h5" fontWeight={800} mb={0.5}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>System configuration for HOA Admin Panel.</Typography>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Left nav */}
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', minWidth: 200, flexShrink: 0, overflow: 'hidden' }}>
          <List dense disablePadding>
            {SECTIONS.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && <Divider />}
                <ListItemButton
                  selected={section === s.id}
                  onClick={() => setSection(s.id)}
                  sx={{ py: 1.5, px: 2, '&.Mui-selected': { backgroundColor: 'primary.main', color: '#fff', '& .MuiListItemIcon-root': { color: '#fff' }, '&:hover': { backgroundColor: 'primary.dark' } } }}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>{s.icon}</ListItemIcon>
                  <ListItemText primary={s.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* Right content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* ── Alerts ── */}
          {section === 'alerts' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <WebhookIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Webhook Alerts</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Microsoft Teams webhook for tenant down / recovery alerts with escalation ladder.
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              {loading ? <CircularProgress size={24} /> : (
                <>
                  <TextField label="Webhook URL" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
                    fullWidth multiline minRows={3} maxRows={6} placeholder="https://..."
                    InputProps={{ endAdornment: webhookUrl ? (
                      <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <Tooltip title="Copy URL"><IconButton size="small" onClick={() => navigator.clipboard.writeText(webhookUrl)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                      </InputAdornment>
                    ) : null }}
                    sx={{ mb: 2 }} />
                  {saveErr    && <Alert severity="error"   sx={{ mb: 2 }}>{saveErr}</Alert>}
                  {saved      && <Alert severity="success" sx={{ mb: 2 }}>Webhook URL saved.</Alert>}
                  {testResult && <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mb: 2 }}>{testResult.message}</Alert>}
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving}>Save</Button>
                    <Button variant="outlined"  startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />} onClick={handleTest} disabled={testing || !webhookUrl}>Test</Button>
                  </Box>
                </>
              )}
            </Paper>
          )}

          {/* ── Security / MFA ── */}
          {section === 'security' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Security & MFA</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                MFA is mandatory for all accounts. Super admins can reset MFA for account recovery.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {loading ? <CircularProgress size={24} /> : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>User</strong></TableCell>
                      <TableCell><strong>Role</strong></TableCell>
                      <TableCell><strong>MFA</strong></TableCell>
                      <TableCell><strong>Last Login</strong></TableCell>
                      {isSuperAdmin && <TableCell><strong>Actions</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                        <TableCell>
                          <Chip label={u.role.replace('_',' ')} size="small"
                            sx={{ height:20, fontSize:'0.7rem', fontWeight:700, backgroundColor:`${ROLE_COLORS[u.role]}18`, color:ROLE_COLORS[u.role], borderRadius:'6px' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={u.mfa_enabled ? 'Enabled' : 'Not set up'} size="small"
                            color={u.mfa_enabled ? 'success' : 'warning'} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                          {u.last_login ? new Date(u.last_login).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : 'Never'}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell>
                            {u.mfa_enabled && (
                              <>
                                <Button size="small" color="error" variant="outlined"
                                  onClick={async () => {
                                    if (!window.confirm(`Reset MFA for ${u.username}?`)) return;
                                    try {
                                      await api.post('/auth/mfa/disable', { targetUserId: u.id });
                                      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, mfa_enabled: 0 } : x));
                                      setMfaMsg(m => ({ ...m, [u.id]: { ok: true, text: 'MFA reset.' } }));
                                    } catch (e) { setMfaMsg(m => ({ ...m, [u.id]: { ok: false, text: e.response?.data?.message || 'Failed' } })); }
                                  }}>Reset MFA</Button>
                                {mfaMsg[u.id] && (
                                  <Typography variant="caption" color={mfaMsg[u.id].ok ? 'success.main' : 'error.main'} ml={1}>{mfaMsg[u.id].text}</Typography>
                                )}
                              </>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          )}

          {/* ── Roles & Permissions matrix ── */}
          {section === 'roles' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <ShieldIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Roles & Permissions</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Default permissions by role. Individual users can have overrides applied in the User Overrides tab.
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight:700, fontSize:'0.8rem', color:'text.secondary', width:'40%' }}>Permission</TableCell>
                    {['readonly','operator','admin','super_admin'].map(r => (
                      <TableCell key={r} align="center" sx={{ fontWeight:700, fontSize:'0.78rem', pb:1.5 }}>
                        <Chip label={r.replace('_',' ')} size="small"
                          sx={{ fontWeight:700, fontSize:'0.7rem', backgroundColor:`${ROLE_COLORS[r]}18`, color:ROLE_COLORS[r], height:22, borderRadius:'6px' }} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PERM_MATRIX.map(p => (
                    <TableRow key={p.key} hover>
                      <TableCell sx={{ fontSize:'0.82rem', py:1 }}>{p.label}</TableCell>
                      {['readonly','operator','admin','super_admin'].map(r => (
                        <TableCell key={r} align="center" sx={{ py:1 }}><Check val={p[r]} /></TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* ── User Overrides ── */}
          {section === 'overrides' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <ManageAccountsIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>User Permission Overrides</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Grant or restrict specific permissions for individual users, overriding their role defaults. Only super admins can set overrides.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {loading ? <CircularProgress size={24} /> : (
                <Box>
                  {users.filter(u => u.role !== 'super_admin').map(u => {
                    const overrides = u.permission_overrides || {};
                    const hasOverrides = Object.keys(overrides).length > 0;
                    const isOpen = expanded === u.id;
                    return (
                      <Paper key={u.id} variant="outlined" sx={{ mb: 1.5, borderRadius: '12px', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                          onClick={() => setExpanded(isOpen ? null : u.id)}>
                          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} fontSize="0.9rem">{u.username}</Typography>
                            <Chip label={u.role.replace('_',' ')} size="small"
                              sx={{ height:20, fontSize:'0.68rem', fontWeight:700, backgroundColor:`${ROLE_COLORS[u.role]}18`, color:ROLE_COLORS[u.role], borderRadius:'6px' }} />
                            {hasOverrides && (
                              <Chip icon={<LockOpenIcon sx={{ fontSize:'14px !important' }} />} label={`${Object.keys(overrides).length} override${Object.keys(overrides).length>1?'s':''}`}
                                size="small" color="warning" sx={{ height:20, fontSize:'0.68rem' }} />
                            )}
                          </Box>
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </Box>
                        <Collapse in={isOpen}>
                          <Divider />
                          <Box sx={{ px: 2, py: 1.5 }}>
                            {!isSuperAdmin && (
                              <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>Only super admins can modify permission overrides.</Alert>
                            )}
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight:700, fontSize:'0.76rem', color:'text.secondary' }}>Permission</TableCell>
                                  <TableCell align="center" sx={{ fontWeight:700, fontSize:'0.76rem', color:'text.secondary' }}>Role Default</TableCell>
                                  <TableCell align="center" sx={{ fontWeight:700, fontSize:'0.76rem', color:'text.secondary' }}>Override</TableCell>
                                  <TableCell align="center" sx={{ fontWeight:700, fontSize:'0.76rem', color:'text.secondary' }}>Effective</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {OVERRIDEABLE.map(p => {
                                  const roleDefault = p[u.role];
                                  const ov = overrides[p.key];
                                  const hasOv = ov !== undefined && ov !== null;
                                  const effective = hasOv ? ov : roleDefault;
                                  return (
                                    <TableRow key={p.key}>
                                      <TableCell sx={{ fontSize:'0.8rem', py:0.75 }}>{p.label}</TableCell>
                                      <TableCell align="center" sx={{ py:0.75 }}><Check val={roleDefault} /></TableCell>
                                      <TableCell align="center" sx={{ py:0.75 }}>
                                        {isSuperAdmin ? (
                                          <Switch
                                            size="small"
                                            checked={hasOv}
                                            disabled={overrideSaving[u.id]}
                                            onChange={() => toggleOverride(u.id, p.key, overrides, roleDefault)}
                                            color={hasOv ? (ov ? 'success' : 'error') : 'default'}
                                          />
                                        ) : (
                                          <Typography variant="caption" color="text.disabled">{hasOv ? (ov ? 'Granted' : 'Denied') : 'None'}</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="center" sx={{ py:0.75 }}>
                                        <Chip
                                          label={effective ? 'Allowed' : 'Denied'}
                                          size="small"
                                          sx={{
                                            height:20, fontSize:'0.68rem', fontWeight:700, borderRadius:'6px',
                                            backgroundColor: effective ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.08)',
                                            color: effective ? '#16a34a' : '#dc2626',
                                            border: hasOv ? `1px solid ${effective ? '#16a34a' : '#dc2626'}` : 'none',
                                          }} />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                            {isSuperAdmin && hasOverrides && (
                              <Button size="small" color="error" sx={{ mt: 1.5, textTransform: 'none' }}
                                onClick={async () => {
                                  setOverrideSaving(s => ({ ...s, [u.id]: true }));
                                  try {
                                    await api.put(`/users/${u.id}`, { permission_overrides: {} });
                                    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, permission_overrides: {} } : x));
                                  } catch { notify('Failed', 'error'); }
                                  finally { setOverrideSaving(s => ({ ...s, [u.id]: false })); }
                                }}>
                                Clear all overrides
                              </Button>
                            )}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}

          {/* ── SSO ── */}
          {section === 'sso' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <WindowIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Microsoft SSO</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Allow users to sign in with their Microsoft / Azure AD account. The user's email must match an existing admin user account.
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              {loading ? <CircularProgress size={24} /> : (
                <>
                  <FormControlLabel
                    control={<Switch checked={sso.enabled} onChange={e => setSso(s => ({ ...s, enabled: e.target.checked }))} />}
                    label={<Typography fontWeight={600}>Enable Microsoft SSO</Typography>}
                    sx={{ mb: 2.5 }}
                  />
                  <Alert severity="info" sx={{ mb: 2.5, borderRadius: '10px' }}>
                    Register an app in <strong>Azure Portal</strong> → App Registrations. Set the redirect URI to your callback URL below, and grant <strong>openid</strong>, <strong>email</strong>, <strong>profile</strong> scopes.
                  </Alert>
                  {[
                    { key: 'client_id',    label: 'Application (Client) ID',  placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
                    { key: 'tenant_id',    label: 'Directory (Tenant) ID',    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx or "common"' },
                    { key: 'client_secret',label: 'Client Secret',            placeholder: 'Value from Azure App → Certificates & secrets', type: 'password' },
                    { key: 'redirect_uri', label: 'Redirect URI (Callback)',  placeholder: 'https://admin.myhoa.club/api/auth/microsoft/callback' },
                    { key: 'frontend_url', label: 'Frontend URL',             placeholder: 'https://admin.myhoa.club' },
                  ].map(f => (
                    <TextField key={f.key} fullWidth size="small" label={f.label} placeholder={f.placeholder}
                      type={f.type || 'text'} value={sso[f.key]}
                      onChange={e => setSso(s => ({ ...s, [f.key]: e.target.value }))}
                      sx={{ mb: 2 }} />
                  ))}

                  {ssoSaved && <Alert severity="success" sx={{ mb: 2 }}>SSO settings saved.</Alert>}

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button variant="contained" startIcon={ssoSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSsoSave} disabled={ssoSaving}>Save SSO Settings</Button>
                    {sso.enabled && sso.client_id && (
                      <Button variant="outlined" startIcon={<WindowIcon />}
                        href="/api/auth/microsoft" target="_blank" rel="noopener">
                        Test SSO Login
                      </Button>
                    )}
                  </Box>

                  {sso.redirect_uri && (
                    <Box sx={{ mt: 2.5, p: 2, backgroundColor: 'action.hover', borderRadius: '10px' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Copy this URI into your Azure App Registration:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', flex: 1, wordBreak: 'break-all' }}>
                          {sso.redirect_uri}
                        </Typography>
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={() => { navigator.clipboard.writeText(sso.redirect_uri); notify('Copied!'); }}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          )}

        </Box>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
